import React from 'react';
import { Button, Switch, Typography, Dropdown, Modal, Input, Space, Form } from 'antd';
import { RightOutlined, CopyOutlined, MoreOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../../../context/ThemeContext';
import {
  StyledCard,
  ActionIconButton,
  TitleNoMargin,
  DuplicateButton,
} from '../Styles';
import EditOptions from '../EditOptions/EditOptions';
import AddToItems from '../AddToItems/AddToItems';
import CustomizationRules from '../CustomizationRules/CustomizationRules';
import { CustomizationSideBoxProps } from '../types';
import { useCustomizationSideBox } from '../hooks/useCustomizationSideBox';

const { Text } = Typography;

const CustomizationSideBox: React.FC<CustomizationSideBoxProps> = ({
  customizationData,
  handleInputChange,
  handleDelete,
  handleDuplicate,
  onClose,
  isOpen,
  handleSubmit,
  setCustomizationData,
  isNewCustomization,
  selectedItem,
  handleRefresh,
  width,
  isEditOption,
  autoEditOptions = false,
  editTargetOptionId = null,
}) => {
  const { isDarkMode } = useTheme();
  const {
    isEditModalOpen,
    isSavingName,
    editOptionsMode,
    isOption,
    addToItemsMode,
    customizationRulesMode,
    soldOut,
    setSoldOut,
    handleEditNameSave,
    openEditModal,
    closeEditModal,
    handleEditOptionsClick,
    handleCustomizationRulesClick,
    handleAddToItemsClick,
    handleBackClick,
    handleStockToggle,
    handleRelatedItemsChange,
  } = useCustomizationSideBox(
    customizationData,
    handleInputChange,
    handleSubmit,
    setCustomizationData,
    selectedItem,
    isEditOption,
    autoEditOptions,
  );

  if (!customizationData) return null;

  if (editOptionsMode || (isEditOption && isOption)) {
    return (
      <EditOptions
        customizationData={customizationData}
        handleBackClick={handleBackClick}
        selectedItem={selectedItem}
        handleRefresh={handleRefresh}
        width={width}
        initialEditOptionId={editTargetOptionId}
      />
    );
  }

  if (addToItemsMode) {
    return (
      <AddToItems
        customizationData={customizationData}
        handleBackClick={handleBackClick}
        handleSubmit={handleSubmit}
        handleRelatedItemsChange={handleRelatedItemsChange}
        width={width}
      />
    );
  }

  if (customizationRulesMode) {
    return (
      <CustomizationRules
        customizationData={customizationData}
        handleBackClick={handleBackClick}
        handleSubmit={handleSubmit}
        width={width}
      />
    );
  }

  const menuItems = [
    { key: 'delete', label: 'Delete', danger: true, onClick: handleDelete },
  ];

  return (
    <StyledCard $width={width} $isFixed={!width}>
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <TitleNoMargin level={4}>Customizations</TitleNoMargin>
          <Space size={8}>
            <DuplicateButton icon={<CopyOutlined />} size="small" onClick={handleDuplicate}>
              Duplicate
            </DuplicateButton>
            <Dropdown menu={{ items: menuItems }} trigger={['click']}>
              <Button type="text" icon={<MoreOutlined />} />
            </Dropdown>
          </Space>
        </div>

        {/* Name */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <Text strong>Customization Name</Text>
            <ActionIconButton onClick={openEditModal}>
              <FontAwesomeIcon icon={faPen} fontSize={14} color={isDarkMode ? '#fff' : '#222'} />
            </ActionIconButton>
          </div>
          <div style={{
            background: isDarkMode ? '#374151' : '#f5f5f5',
            border: `1px solid ${isDarkMode ? '#4a5568' : '#e8e8e8'}`,
            padding: '8px 12px',
            borderRadius: 6,
          }}>
            <Text>{customizationData.customization}</Text>
          </div>
        </div>

        {/* Navigation */}
        <Space direction="vertical" size={4} style={{ width: '100%', marginBottom: 16 }}>
          <Button
            block
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 40 }}
            onClick={handleEditOptionsClick}
          >
            Edit options <RightOutlined />
          </Button>
          <Text type="secondary" style={{ fontSize: 12, paddingInline: 8, display: 'block' }}>
            Manage which options are available to customers in this set.
          </Text>

          <Button
            block
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 40, marginTop: 8 }}
            onClick={handleAddToItemsClick}
          >
            Add to items <RightOutlined />
          </Button>

          <Button
            block
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 40, marginTop: 4 }}
            onClick={handleCustomizationRulesClick}
          >
            Customisation rules <RightOutlined />
          </Button>
        </Space>

        {/* Sold out */}
        <div>
          <Text type="danger" strong style={{ display: 'block', marginBottom: 6 }}>Sold out</Text>
          <Switch
            checked={soldOut}
            onChange={async (checked) => {
              setSoldOut(checked);
              handleInputChange('sold_out', checked);
              if (selectedItem) await handleStockToggle(selectedItem, checked);
            }}
          />
        </div>
      </div>

      {/* Edit Name Modal */}
      <Modal
        title="Edit Customization Name"
        open={isEditModalOpen}
        onCancel={closeEditModal}
        centered
        width={560}
        footer={[
          <Button key="cancel" onClick={closeEditModal}>Cancel</Button>,
          <Button key="save" type="primary" loading={isSavingName} onClick={handleEditNameSave}
            style={{ backgroundColor: '#2D6CCA', borderColor: '#2D6CCA' }}>
            Save
          </Button>,
        ]}
      >
        <Form layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item label="Customization Name" required>
            <Input
              placeholder="Enter customization name"
              value={customizationData.customization}
              onChange={(e) => handleInputChange('customization', e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Create / Edit Modal */}
      <Modal
        title={isNewCustomization ? 'Create Customization' : 'Edit Customization'}
        open={isOpen && isNewCustomization}
        onCancel={onClose}
        centered
        width={560}
        footer={[
          <Button key="save" type="primary" onClick={() => handleSubmit('create', customizationData)}
            style={{ backgroundColor: '#2D6CCA', borderColor: '#2D6CCA' }}>
            Save
          </Button>,
          <Button key="cancel" onClick={onClose}>Cancel</Button>,
        ]}
      >
        <Form layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item label="Customization Name" required>
            <Input
              placeholder="Enter customization name"
              value={customizationData.customization}
              onChange={(e) => handleInputChange('customization', e.target.value)}
            />
          </Form.Item>
          <Form.Item label="Note">
            <Input
              placeholder="Optional note"
              value={customizationData.note}
              onChange={(e) => handleInputChange('note', e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>
    </StyledCard>
  );
};

export default CustomizationSideBox;
